import cx from "classnames";
import { ButtonCompound } from "../Button";
import { Card, CardProps } from "../Card";
import { useDndContext } from "@dnd-kit/core";
import { useDndSortContext } from "./internal/DndSortContext";
import { ReactNode, useCallback, useEffect } from "react";
import { DndSortChangeEvent } from "./types";
import DndSortItem from "./internal/DndSortItem";
import { DndHandleButton } from "../Dnd/DndHandleButton";
import { DndHandle } from "../Dnd/DndHandle";
import dndSortStyles from "./DndSort.module.scss";

/**
 * Props for the DndSortCard component
 * @extends Omit<CardProps, "id">
 */
export type DndSortCardProps = Omit<CardProps, "id"> & {
  /**
   * The content to be displayed inside the card.
   */
  children: React.ReactNode;
  /**
   * If true, the card can only be dragged when the drag handle is used.
   * If false, the entire card can be dragged.
   * @default false
   */
  dragOnlyWithHandle?: boolean;
  /**
   * The unique identifier for the card.
   */
  id: string | number;
  /**
   * The label for the card, used for accessibility.
   */
  label: string;
  /**
   * Callback function that is called when the card is dropped.
   */
  onDrop?: (event: DndSortChangeEvent) => void;
  /**
   * The type of the card, used for validation.
   */
  type?: string;
  /**
   * Function to render a custom preview of the card when it's being dragged.
   */
  previewRenderer?: () => React.ReactNode;
};

/**
 * A card component that can be dragged and dropped using the DndSort system.
 *
 * Features:
 * - Draggable card with visual feedback
 * - Optional drag handle for controlled dragging
 * - Custom preview rendering during drag
 * - Type-based validation support
 * - Accessible with proper ARIA attributes
 * - Extends Card component functionality
 * - Supports both handle-only and full-card dragging
 * - Visual feedback for active drag state
 * - Flexible content rendering
 *
 * @example
 * <DndSortCard
 *   id="card-1"
 *   label="Card 1"
 *   dragOnlyWithHandle={true}
 *   onDrop={handleCardDrop}
 * >
 *   Card content here
 * </DndSortCard>
 */
export default function DndSortCard({
  children,
  dragOnlyWithHandle = false,
  id,
  label,
  onDrop,
  type,
  previewRenderer: previewRendererProp,
  ...rest
}: DndSortCardProps) {
  const { active } = useDndContext();
  const { setPreviewRenderer } = useDndSortContext();

  const isActive = active?.id === id; // Check if this card is currently being dragged
  const cardClasses = cx(rest.className, dndSortStyles["card"], {
    [dndSortStyles["active"]]: isActive,
    [dndSortStyles["card-as-button"]]: !dragOnlyWithHandle,
  });

  const buttonWrapperClasses = cx(dndSortStyles["card-button-wrapper"], {
    [dndSortStyles["card-as-button"]]: !dragOnlyWithHandle,
  });

  const previewRenderer = useCallback(() => {
    // This function will be called to render the preview of the card when it's being dragged
    // You can customize this to return any JSX you want to display as the preview
    return <DndSortCardPreview {...rest}>{children}</DndSortCardPreview>;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't want to re-render the preview when the rest props change
  }, []);

  // When this card is being dragged, we want to set the preview renderer in the context
  // This allows the preview to be displayed correctly while dragging
  useEffect(() => {
    if (active?.id === id) {
      setPreviewRenderer(() => previewRendererProp ?? previewRenderer);
    }
  }, [active, id, previewRenderer, previewRendererProp, setPreviewRenderer]);

  if (dragOnlyWithHandle) {
    return (
      <DndSortItem
        id={id}
        label={label}
        onDrop={onDrop}
        type={type}
        data-testid={`draggable-${id}`}
      >
        {({
          setActivatorNodeRef,
          setNodeRef,
          attributes,
          listeners,
          style,
        }) => (
          <Card
            ref={setNodeRef}
            {...rest}
            data-anv="draggable-card"
            data-testid={`drag-and-drop-draggable-${id}`}
            className={cardClasses}
          >
            <DndHandleButton
              ref={setActivatorNodeRef}
              {...attributes}
              {...listeners}
              style={style}
              className={dndSortStyles["card-handle"]}
              isActive={isActive}
            />
            {children}
          </Card>
        )}
      </DndSortItem>
    );
  }

  return (
    <>
      <DndSortItem id={id} label={label} onDrop={onDrop} type={type}>
        {({ setNodeRef, attributes, listeners, style }) => (
          <ButtonCompound
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            style={style}
            className={buttonWrapperClasses}
            type="button"
            shape="rounded"
            data-testid={`drag-and-drop-draggable-${id}`}
          >
            <Card {...rest} data-anv="draggable-card" className={cardClasses}>
              <DndHandle className={dndSortStyles["card-handle"]} />
              {children}
            </Card>
          </ButtonCompound>
        )}
      </DndSortItem>
    </>
  );
}

/**
 * Sub-component for rendering the preview of the card when it's being dragged
 * @param props
 * @param props.children The content to display inside the preview card
 * @param props.rest Additional props to pass to the card component
 *
 * @returns
 */
const DndSortCardPreview = ({
  children,
  ...rest
}: { children: ReactNode } & CardProps) => {
  const cardClasses = cx(dndSortStyles["card"], dndSortStyles["preview"]);
  return (
    <Card
      {...rest}
      data-anv="draggable-card"
      className={cardClasses}
      data-testid="drag-and-drop-overlay"
    >
      <DndHandle className={dndSortStyles["card-handle"]} />
      {children}
    </Card>
  );
};
